import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCartData, useServiceablitityData } from '../../../hooks/order';
import useWindowDimensions from '../../common/useWindowDimensions';
import { saveInternetPlan } from '../../../store/action/OrderAction';
import { getProductPrice, getPromoPrice } from '../../../common/common';
import { getPlanFeatures, getPlanName, resolvePlanName } from '../../../helpers/planTranslations';
import { getProductLabel } from '../../../helpers/productLabels';
import { getDefaultPlan } from '../../../helpers/defaultPlan';
import { DownloadUploadSpeedIcon, UnlimitedDataIcon, CustomerSupportIcon, LifeLockIcon, CalendarIcon } from '../../../images/svg';
import SpeedGuideModal from './SpeedGuideModal';

const FEATURE_ICONS = [DownloadUploadSpeedIcon, UnlimitedDataIcon, CustomerSupportIcon, LifeLockIcon, CalendarIcon];

const ProductSelectionSection = ({ confirmed, onConfirm, onChange, changeDisabled }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { internetPlan, isInternetAdded, spanishLanguagePref } = useCartData();
  const { allPlans, serviceabilityCheck } = useServiceablitityData();
  const { fixedWirelessProducts = [], serviceTerm } = serviceabilityCheck;
  const { isMobile } = useWindowDimensions();
  const [showSpeedGuide, setShowSpeedGuide] = useState(false);
  const [showMobileDetails, setShowMobileDetails] = useState(false);

  const sortedPlans = useMemo(() => {
    const withSpeed = allPlans?.filter((p) => p.downstreamSpd) || [];
    const withoutSpeed = allPlans?.filter((p) => !p.downstreamSpd) || [];
    return [...withSpeed].sort((a, b) => Number(a.downstreamSpd) - Number(b.downstreamSpd)).concat(withoutSpeed);
  }, [allPlans]);

  // A plan is always pre-selected before this page is reached (ServiceabilityCheck.js), but
  // fall back to the same default-plan rule here so the card never renders empty.
  const defaultPlan = useMemo(() => getDefaultPlan(sortedPlans, serviceTerm), [sortedPlans, serviceTerm]);

  useEffect(() => {
    if (!isInternetAdded && defaultPlan) {
      dispatch(saveInternetPlan(defaultPlan));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInternetAdded, defaultPlan]);

  const selectedPlan = isInternetAdded ? internetPlan : defaultPlan;

  if (!selectedPlan) {
    return null;
  }

  const promoPrice = getPromoPrice(selectedPlan, serviceTerm);
  const regularPrice = getProductPrice(selectedPlan, serviceTerm);
  const displayPrice = promoPrice || regularPrice;
  // getPlanName() prefers the raw downstreamSpeed field (e.g. "100Mb"), which is the right
  // call for the compact tier buttons below but reads wrong here - the Figma featured-plan
  // headline wants the fuller catalog name (e.g. "Fiber 100 Mbps"), so this one call site
  // prefers serviceName instead, falling back to getPlanName only if it's missing.
  const planName = resolvePlanName(spanishLanguagePref, selectedPlan.serviceName)
    || getPlanName({ plan: selectedPlan, spanishLanguagePref });
  const planLabel = getProductLabel(selectedPlan, t);
  const features = getPlanFeatures({ plan: selectedPlan, spanishLanguagePref, fixedWirelessProducts });

  const handleSelectTier = (plan) => {
    dispatch(saveInternetPlan(plan));
  };

  return (
    <div className={`spc-card spc-product-section ${confirmed ? 'is-collapsed' : 'is-expanded'}`}>
      {confirmed ? (
        <div className="spc-card__collapsed-row">
          <div className="spc-collapsed-plan spc-collapsed-appointment">
            <span className="spc-collapsed-plan__title">{t('singlePageCheckout.yourInternetPlan')} -</span>
            <span className="spc-collapsed-plan__summary">{planName} &mdash; {displayPrice} {t('singlePageCheckout.perMonth')}</span>
          </div>
          <button type="button" className="spc-link-button spc-change-link" onClick={onChange} disabled={changeDisabled}>
            {t('common.change')}
          </button>
        </div>
      ) : (
        <>
          <div className="spc-card__header">
            <h3 className="spc-card__title">{t('singlePageCheckout.yourInternetPlan')}</h3>
            <p className="spc-card__subtitle">{t('singlePageCheckout.selectProductSubtitle')}</p>
          </div>

          <div className="spc-selected-plan">
            {planLabel && <span className="spc-badge">{planLabel}</span>}
            <div className="spc-selected-plan__body">
              <div className="spc-selected-plan__info">
                <h4 className="spc-selected-plan__name">{planName}</h4>
                <div className="spc-selected-plan__price">
                  {displayPrice} / {t('common.month')}
                </div>
                {isMobile && !showMobileDetails && features[0] && (
                  <p className="spc-selected-plan__first-feature">{features[0]}</p>
                )}
                {isMobile && (
                  <button
                    type="button"
                    className="spc-see-details-link"
                    onClick={() => setShowMobileDetails((prev) => !prev)}
                  >
                    <span>{showMobileDetails ? t('singlePageCheckout.hidePlanDetails') : t('singlePageCheckout.seePlanDetails')}</span>
                    <i className={`fa-solid fa-caret-${showMobileDetails ? 'up' : 'down'}`}></i>
                  </button>
                )}
              </div>

              {(!isMobile || showMobileDetails) && (
                <ul className="spc-feature-grid">
                  {(isMobile ? features : features.slice(0, 4)).map((feature, index) => {
                    const Icon = FEATURE_ICONS[index];
                    return (
                      <li className="spc-feature-grid__item" key={`feature-${index}`}>
                        {Icon && <span className="spc-feature-grid__icon"><Icon /></span>}
                        <span>{feature}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className={`spc-tier-row ${isMobile ? 'spc-tier-row--mobile' : ''}`}>
            {sortedPlans.map((plan) => {
              const tierPromo = getPromoPrice(plan, serviceTerm);
              const tierPrice = tierPromo || getProductPrice(plan, serviceTerm);
              const isSelected = selectedPlan?.servRef === plan.servRef;
              return (
                <button
                  type="button"
                  key={plan.servRef}
                  className={`spc-tier-button ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => handleSelectTier(plan)}
                >
                  <span className="spc-tier-button__name">{getPlanName({ plan, spanishLanguagePref })}</span>
                  <span className="spc-tier-button__price">{tierPrice} {t('singlePageCheckout.perMonth')}</span>
                </button>
              );
            })}
          </div>

          <div className="spc-card__footer">
            {!isMobile && (
              <button type="button" className="spc-link-button spc-compare-link" onClick={() => setShowSpeedGuide(true)}>
                {t('singlePageCheckout.compareAllPlansPrompt')}
              </button>
            )}
            <button type="button" className="blue-button spc-select-button" onClick={onConfirm}>
              {t('singlePageCheckout.selectThisPlan')}
            </button>
          </div>
        </>
      )}

      {showSpeedGuide && (
        <SpeedGuideModal
          plans={sortedPlans}
          serviceTerm={serviceTerm}
          spanishLanguagePref={spanishLanguagePref}
          selectedServRef={selectedPlan?.servRef}
          fixedWirelessProducts={fixedWirelessProducts}
          onClose={() => setShowSpeedGuide(false)}
        />
      )}
    </div>
  );
};

export default ProductSelectionSection;
